import type { ICompetencyItem } from 'src/types/competency';

import { useMemo } from 'react';
import useSWR from 'swr';

import { endpoints, fetcher } from 'src/utils/axios';
import { mergeCompetencyLists } from 'src/utils/competency';

import { useAuthContext } from 'src/auth/hooks';

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

type CompetencysData = {
  competencies: ICompetencyItem[];
};

export function useGetCompetencys(options?: { enabled?: boolean }) {
  const url = options?.enabled !== false ? endpoints.competency.list : null;

  const { data, isLoading, error, isValidating } = useSWR<CompetencysData>(
    url,
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      competencies: data?.competencies || [],
      competencysLoading: isLoading,
      competencysError: error,
      competencysValidating: isValidating,
      competencysEmpty: !isLoading && !data?.competencies.length,
    }),
    [data?.competencies, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetCompetencyForClient() {
  const { user } = useAuthContext();
  const url = `${endpoints.competency.list}?clientId=${user?.client?.id}`;

  const { data, isLoading, error, isValidating, mutate } = useSWR<CompetencysData>(
    url,
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      competencies: data?.competencies || [],
      competencysLoading: isLoading,
      competencysError: error,
      competencysValidating: isValidating,
      competencysEmpty: !isLoading && !data?.competencies,
      mutate,
    }),
    [data?.competencies, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetCompetencysForSpecificClient(user: any) {
  console.log('jnkk', user);
  // console.log('jnkk', user?.client?.id);

  // const url = endpoints.competency.list + '?clientId=1';
  const url = `${endpoints.competency.list}?clientId=${user?.client?.id}`;

  const { data, isLoading, error, isValidating } = useSWR<CompetencysData>(
    url,
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      competencies: data?.competencies || [],
      competencysLoading: isLoading,
      competencysError: error,
      competencysValidating: isValidating,
      competencysEmpty: !isLoading && !data?.competencies,
    }),
    [data?.competencies, error, isLoading, isValidating]
  );

  return memoizedValue;
}

type CompetencyData = {
  competency: ICompetencyItem;
};

export function useGetCompetency(competencyId: string) {
  const url = competencyId ? [endpoints.competency.details, { params: { competencyId } }] : '';

  const { data, isLoading, error, isValidating } = useSWR<CompetencyData>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(
    () => ({
      competency: data?.competency,
      competencyLoading: isLoading,
      competencyError: error,
      competencyValidating: isValidating,
    }),
    [data?.competency, error, isLoading, isValidating]
  );

  return memoizedValue;
}

type SearchResultsData = {
  results: ICompetencyItem[];
};

export function useSearchCompetencys(query: string) {
  const url = query ? [endpoints.competency.search, { params: { query } }] : '';

  const { data, isLoading, error, isValidating } = useSWR<SearchResultsData>(url, fetcher, {
    ...swrOptions,
    keepPreviousData: true,
  });

  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.results || [],
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
      searchEmpty: !isLoading && !data?.results.length,
    }),
    [data?.results, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetCompetencyForStandardTemplate(standardTemplateId?: number) {
  const url = standardTemplateId ? `${endpoints.scheme.competencies(standardTemplateId)}` : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR<ICompetencyItem[]>(
    url,
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      standardCompetencies: data || [],
      standardCompetenciesLoading: isLoading,
      standardCompetenciesError: error,
      standardCompetenciesValidating: isValidating,
      standardCompetenciesEmpty: !isLoading && !data,
      standardCompetenciesMutate: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetCompetencyForStandardTemplates(standardTemplateIds?: number[]) {
  const normalizedTemplateIds = useMemo(
    () =>
      Array.from(
        new Set((standardTemplateIds ?? []).filter((templateId): templateId is number => templateId != null)),
      ).sort((a, b) => a - b),
    [standardTemplateIds],
  );

  const swrKey =
    normalizedTemplateIds.length > 0
      ? ['standard-template-competencies', ...normalizedTemplateIds]
      : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR<ICompetencyItem[]>(
    swrKey,
    async () => {
      const competencyLists = await Promise.all(
        normalizedTemplateIds.map((templateId) =>
          fetcher(`${endpoints.scheme.competencies(templateId)}`) as Promise<ICompetencyItem[]>,
        ),
      );

      return mergeCompetencyLists(competencyLists);
    },
    swrOptions,
  );

  const memoizedValue = useMemo(
    () => ({
      standardCompetencies: data || [],
      standardCompetenciesLoading: isLoading,
      standardCompetenciesError: error,
      standardCompetenciesValidating: isValidating,
      standardCompetenciesEmpty: !isLoading && !data,
      standardCompetenciesMutate: mutate,
    }),
    [data, error, isLoading, isValidating, mutate],
  );

  return memoizedValue;
}
